import { useEffect, useRef } from "react";

// Deterministic permutation table (256), duplicated to 512.
const PERM = (() => {
  const perm = Array.from({ length: 256 }, (_, i) => i);
  let seed = 1337;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [perm[i], perm[j]] = [perm[j], perm[i]];
  }
  const p = new Uint8Array(512);
  for (let i = 0; i < 512; i++) p[i] = perm[i & 255];
  return p;
})();

const fade = (t) => t * t * (3 - 2 * t); // 3t² - 2t³
const lerp = (a, b, t) => a + (b - a) * t;
const valueAt = (ix, iy) => PERM[(PERM[ix & 255] + (iy & 255)) & 255] / 255;

function vnoise(x, y) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = fade(x - x0);
  const fy = fade(y - y0);
  const v00 = valueAt(x0, y0);
  const v10 = valueAt(x0 + 1, y0);
  const v01 = valueAt(x0, y0 + 1);
  const v11 = valueAt(x0 + 1, y0 + 1);
  return lerp(lerp(v00, v10, fx), lerp(v01, v11, fx), fy);
}

// fbm - 4 octaves, gain .5, lacunarity 2.05
function fbm(x, y) {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < 4; o++) {
    sum += amp * vnoise(x * freq, y * freq);
    norm += amp;
    amp *= 0.5;
    freq *= 2.05;
  }
  return sum / norm;
}

export default function CanvasBackground({ theme }) {
  const canvasRef = useRef(null);
  const offRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const off = document.createElement("canvas");
    const octx = off.getContext("2d");
    offRef.current = off;
    let W = 0;
    let H = 0;

    const dark = theme === "dark";
    // base ink colour of the active theme
    const base = dark ? [10, 10, 11] : [237, 236, 232];

    const renderNoise = () => {
      // half resolution + 40px margin
      const ow = Math.ceil(W / 2) + 40;
      const oh = Math.ceil(H / 2) + 40;
      off.width = ow;
      off.height = oh;
      const img = octx.createImageData(ow, oh);
      const data = img.data;
      for (let y = 0; y < oh; y++) {
        for (let x = 0; x < ow; x++) {
          const nx = x * 0.052;
          const ny = y * 0.052;
          const wx = fbm(nx + 5.2, ny + 1.3);
          const wy = fbm(nx + 9.7, ny + 4.1);
          const v = fbm(nx + 3.2 * wx, ny + 3.2 * wy);
          let t = Math.abs(((v * 16) % 1) * 2 - 1);
          t = Math.pow(t, 3.4);
          const amt = dark ? t * 13 : -t * 15;
          const idx = (y * ow + x) * 4;
          data[idx] = base[0] + amt;
          data[idx + 1] = base[1] + amt;
          data[idx + 2] = base[2] + amt;
          data[idx + 3] = 255;
        }
      }
      octx.putImageData(img, 0, 0);
    };

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      renderNoise();
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(off, -40, -40, off.width * 2, off.height * 2);
    };

    let last = 0;
    const draw = (now) => {
      rafRef.current = requestAnimationFrame(draw);
      if (now - last < 1000 / 16) return; // ~16 fps
      last = now;
      const s = now / 1000;
      const dx = Math.sin(s * 0.2) * 16;
      const dy = Math.cos(s * 0.16) * 16;
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(off, -40 + dx, -40 + dy, off.width * 2, off.height * 2);
    };

    resize();

    if (reduce) {
      drawStatic();
    } else {
      rafRef.current = requestAnimationFrame(draw);
    }

    let debounce;
    const onResize = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        resize();
        if (reduce) drawStatic();
      }, 180);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(debounce);
      window.removeEventListener("resize", onResize);
    };
  }, [theme]);

  return <canvas ref={canvasRef} className="bg-canvas" aria-hidden="true" />;
}
